import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Calendar,
  Sun,
  Heart,
  Briefcase,
  Activity,
  DollarSign,
  Star,
  Crown,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const MonthlyForecastPage = () => {
  const navigate = useNavigate();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const response = await api.get("/premium/monthly-forecast");
        setForecast(response.data);
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error("Premium subscription required");
          navigate("/premium");
        } else if (error.response?.status === 400) {
          toast.error("Please complete your profile with date of birth");
          navigate("/profile");
        } else {
          console.error("Error fetching forecast:", error);
          toast.error("Failed to load monthly forecast");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-white/70 font-body">Reading the cosmic calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="monthly-forecast-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/premium")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
                <Calendar className="w-4 h-4 text-[#070B14]" />
              </div>
              <span className="font-display text-xl text-white">Monthly Forecast</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] font-body">Premium</span>
          </div>
        </div>
      </header>

      {forecast && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          {/* Month Header */}
          <div className="text-center animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
              <Sun className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm text-[#D4AF37] font-body">{forecast.sun_sign}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-white mb-2">
              {forecast.month} {forecast.year}
            </h1>
            <p className="text-white/60 font-body">Your complete monthly cosmic guide</p>
          </div>

          {/* Overall Theme */}
          <div className="gold-accent-card p-6 sm:p-8 text-center animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">"{forecast.overall_theme}"</h2>
            <p className="text-white/70 font-body leading-relaxed whitespace-pre-line">{forecast.energy_forecast}</p>
          </div>

          {/* Key Dates */}
          {forecast.key_dates && forecast.key_dates.length > 0 && (
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#C4B5FD]" />
                </div>
                <h3 className="font-display text-xl text-white">Key Dates</h3>
              </div>
              <div className="space-y-3">
                {forecast.key_dates.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-3 rounded-xl bg-white/5">
                    <span className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm font-body font-medium whitespace-nowrap">
                      {item.date}
                    </span>
                    <p className="text-white/80 font-body text-sm">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Life Area Forecasts */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Love */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-pink-400" />
                </div>
                <h3 className="font-display text-lg text-white">Love & Relationships</h3>
              </div>
              <p className="text-white/70 font-body text-sm leading-relaxed">{forecast.love_forecast}</p>
            </div>

            {/* Career */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-display text-lg text-white">Career & Ambitions</h3>
              </div>
              <p className="text-white/70 font-body text-sm leading-relaxed">{forecast.career_forecast}</p>
            </div>

            {/* Health */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="font-display text-lg text-white">Health & Wellness</h3>
              </div>
              <p className="text-white/70 font-body text-sm leading-relaxed">{forecast.health_forecast}</p>
            </div>

            {/* Financial */}
            <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.35s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="font-display text-lg text-white">Finance & Resources</h3>
              </div>
              <p className="text-white/70 font-body text-sm leading-relaxed">{forecast.financial_forecast}</p>
            </div>
          </div>

          {/* Spiritual Message */}
          <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#C4B5FD]" />
              </div>
              <h3 className="font-display text-xl text-white">Spiritual Message</h3>
            </div>
            <p className="text-white/80 font-body leading-relaxed">{forecast.spiritual_message}</p>
          </div>

          {/* Monthly Mantra */}
          <div className="gold-accent-card p-6 sm:p-8 text-center animate-fade-in-up" style={{ animationDelay: "0.45s" }}>
            <Star className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-body mb-3">Monthly Mantra</p>
            <p className="font-display text-xl sm:text-2xl text-white italic">"{forecast.monthly_mantra}"</p>
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up" style={{ animationDelay: "0.5s" }}>
            <Button
              onClick={() => navigate("/premium/weekly-forecast")}
              className="btn-secondary flex-1"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Weekly Forecast
            </Button>
            <Button
              onClick={() => navigate("/premium/birth-chart")}
              className="btn-gold flex-1"
            >
              <Star className="w-5 h-5 mr-2" />
              Birth Chart
            </Button>
          </div>
        </main>
      )}
    </div>
  );
};

export default MonthlyForecastPage;
